<template>
  <UIModalContainer
    :modal-show="modalShow"
    footer-classes="flex justify-end"
    @handleClick="handleClick"
  >
    <template v-slot:header>
      <span v-if="periodData.id" v-i18n="schedule">EDIT_CURRENT_TIME_TABLE</span>
      <span v-else v-i18n="schedule">ADD_CURRENT_TIME_TABLE</span>
    </template>
    <template v-slot>
      <ValidationObserver @submit="addNewSchedule">
        <InputFieldWrapper>
          <UiSingleSelect
            v-model="periodData.subject_id"
            title="SUBJECT"
            :options="periodInfo.subjects"
            label="title"
            rules="required"
            reduce="id"
            :search-through-api="true"
            @search="handleSubjectSearch"
            @change="handleSubjectChange"
          />
          <UiSingleSelect
            v-model="periodData.teacher_id"
            title="Teacher"
            :disabled="disabled"
            :options="periodInfo.teachers"
            label="full_name"
            reduce="id"
            :image="true"
          />
          <UiDateTimePicker
            v-model="periodData.start_time"
            title="Start time"
            name="Start time"
            type="time"
            rules="required"
          />
          <UiDateTimePicker
            v-model="periodData.end_time"
            title="End time"
            name="End time"
            type="time"
            rules="required"
          />
          <UiSingleSelect
            v-model="periodData.room_id"
            title="Room"
            :options="periodInfo.rooms"
            label="title"
            reduce="id"
          />
        </InputFieldWrapper>
        <button ref="submit" type="submit" class="hidden">Submit</button>
      </ValidationObserver>
    </template>
    <template v-slot:footer>
      <div class="flex gap-3 rtl:gap-x-4">
        <UIButton :disabled="isLoading" @click="handleClick('close')">Cancel</UIButton>
        <UIButton button="primary" @click="$refs.submit.click()">
          <template v-if="isLoading">
            <Loader></Loader>
          </template>
          <span v-else>
            {{ primaryButtonText }}
          </span>
        </UIButton>
      </div>
    </template>
  </UIModalContainer>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import UiDateTimePicker from '@src/components/UiElements/UIDateTimePicker.vue'
import UIModalContainer from '@components/UiElements/UiModalContainer.vue'
import { Form as ValidationObserver } from 'vee-validate'
import UIButton from '@src/components/UiElements/UIButton.vue'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { formatDate } from '@utils/moment.util'
import { objectDeepCopy } from '@/src/utils/generalUtil'
import { fullName } from '@src/utils/settings/tenant-user.util.js'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'

export default {
  name: 'AddEditPeriodModal',
  components: {
    ValidationObserver,
    UiSingleSelect,
    UiDateTimePicker,
    UIModalContainer,
    UIButton,
    InputFieldWrapper,
    Loader,
  },
  props: {
    modalShow: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      disabled: false,
      isLoading: false,
      schedule: 'schedule',
      periodInfo: {
        subjects: [],
        teachers: [],
        rooms: [],
      },
      periodData: {
        start_time: null,
        end_time: null,
        room_id: null,
        subject_id: null,
        section_id: null,
        teacher_id: null,
        is_active: true,
      },
    }
  },
  computed: {
    primaryButtonText() {
      return this.periodData.id ? 'Update' : 'Save'
    },
    isSectionTeacher() {
      return this.activeRole === TENANT_ROLES.SECTION_TEACHER
    },
    ...mapState({
      activeRole: (state) => state.layout.activeRole,
      currentUser: (state) => state.auth.currentUser,
      currentSectionScope: (state) => state.layout.currentSectionScope?.id,
    }),
    ...mapGetters('layout', ['campusTimeZone']),
  },
  watch: {
    modalShow(status) {
      if (status) {
        if (!this.editData) this.resetPeriodData()
        this.initCreateSchedule()
      } else {
        this.periodInfo.teachers = []
      }
    },
    editData: {
      handler(data) {
        if (this.modalShow) {
          data ? this.loadEditData(data) : this.resetPeriodData()
        }
      },
    },
  },
  methods: {
    handleClick(eventType) {
      if (eventType === 'close') this.closeModal()
    },
    closeModal() {
      this.$store.dispatch('layout/setShowModal', false)
      this.$emit('close')
    },

    async loadEditData(event) {
      const sectionId = event?.section_id || this.currentSectionScope
      const [subjectsRooms, teachers] = await Promise.all([
        this.getSubjectsRoomsForClass(sectionId),
        this.getTeachersForSubject(event.subject.subjectId),
      ])
      this.periodInfo.subjects = subjectsRooms.subjects
      this.periodInfo.rooms = subjectsRooms.rooms
      this.periodInfo.teachers = teachers.teachers
      Object.assign(this.periodData, {
        id: event?.id,
        section_id: sectionId,
        subject_id: event?.subject?.subjectId,
        room_id: event?.room_id,
        teacher_id: event?.teacher_id,
        start_time: `${event?.date}T${event?.startTime}${this.campusTimeZone}`,
        end_time: `${event?.endDate}T${event?.endTime}${this.campusTimeZone}`,
      })
    },

    async getSubjectsRoomsForClass(currentSectionScope, searchQuery = '') {
      const query = {
        $where: {
          ...(this.isSectionTeacher
            ? buildWhereQuery(FILTER_KEYS.IN, 'assigned_teachers_ids', [this.currentUser?.id])
            : {}),
          ...buildWhereQuery(FILTER_KEYS.EQUAL, 'section_id', currentSectionScope),
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'title', searchQuery),
        },
      }
      const [subjectsRes, roomsRes] = await Promise.all([
        this.classSubjects(query),
        this.getRoomsForClass(query),
      ])
      return {
        subjects: subjectsRes.data.records,
        rooms: roomsRes.data,
      }
    },

    async getTeachersForSubject(subject, searchQuery = '') {
      if (this.isSectionTeacher) return { teachers: [] }
      const resp = await this.getSectionSubjectTeacher({
        $where: {
          ...buildWhereQuery(FILTER_KEYS.EQUAL, 'subject_id', subject),
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'title', searchQuery || subject),
        },
      })
      const teachers = resp?.data?.records || []
      teachers.forEach((t) => (t.full_name = `${t.first_name} ${t.last_name}`))
      return { teachers }
    },

    resetPeriodData() {
      Object.assign(this.periodData, {
        start_time: null,
        end_time: null,
        room_id: null,
        subject_id: null,
        section_id: this.currentSectionScope,
        teacher_id: null,
        is_active: true,
      })
      delete this.periodData.id
    },

    async initCreateSchedule() {
      if (this.currentSectionScope) {
        this.periodData.section_id = this.currentSectionScope
        const data = await this.getSubjectsRoomsForClass(this.currentSectionScope)
        this.periodInfo.subjects = data.subjects
        this.periodInfo.rooms = data.rooms
      }
      if (this.isSectionTeacher) {
        this.disabled = true
        const user = objectDeepCopy(this.currentUser)
        fullName(user)
        this.periodInfo.teachers.push(user)
        this.periodData.teacher_id = user?.id
      }
    },

    async handleSubjectSearch(searchQuery) {
      const data = await this.getSubjectsRoomsForClass(this.currentSectionScope, searchQuery)
      this.periodInfo.subjects = data.subjects
      this.periodInfo.rooms = data.rooms
    },
    async handleSubjectChange() {
      if (!this.periodData.subject_id) return
      this.periodData.teacher_id = null
      const data = await this.getTeachersForSubject(this.periodData.subject_id)
      this.periodInfo.teachers = data.teachers
    },

    async addNewSchedule() {
      this.isLoading = true
      if (!this.periodData.section_id && this.currentSectionScope) {
        this.periodData.section_id = this.currentSectionScope
      }
      let subject_id = this.periodData.subject_id
      if (subject_id && typeof subject_id === 'object' && subject_id.id) {
        subject_id = subject_id.id
      }
      const { start_time, end_time, teacher_id, room_id, section_id } = this.periodData
      let sectionId = section_id || this.currentSectionScope
      if (sectionId && typeof sectionId === 'object' && sectionId.id) {
        sectionId = sectionId.id
      }
      try {
        await this.addPeriod({
          routine: {
            day: formatDate(start_time, 'dddd').toLowerCase(),
            started_at: start_time,
            ended_at: end_time,
            subject_id: subject_id,
            section_id: sectionId,
            is_active: true,
            date: start_time,
            ...(teacher_id && { teacher_id: Number(teacher_id) }),
            ...(room_id && { room_id: Number(room_id) }),
          },
          id: this.periodData.id || null,
        })
        this.closeModal()
        this.$emit('saved')
      } catch (error) {
        // Error handled silently
      } finally {
        this.isLoading = false
      }
    },

    ...mapActions('period', ['addPeriod', 'getSectionSubjectTeacher']),
    ...mapActions('routine', ['getRoomsForClass']),
    ...mapActions('subjects', ['classSubjects']),
  },
}
</script>
